/** Top road section of the LUDOKA game board - PC skin (Blue/Function keys keyboard) */

import type { RoadSectionProps } from '../../constants/types';
import { renderMultiplePositions } from '../basics/utils/utils';
import { TOP_CENTER_SQUARE_BOUNDS as CENTER_SQUARE_BOUNDS } from '../basics/constants/center-square-bounds';
import type { BoardPosition } from '../../constants/positions';

/**
 * Render order for top section (Blue)
 * Ordered top to bottom for proper 3D overlap
 */
const RENDER_ORDER: BoardPosition[] = [
  'BS1',
  'BC1',
  'BF1',
  'BS2',
  'BC2',
  'BF2',
  'BS3',
  'BC3',
  'BF3',
  'BS4',
  'BC4',
  'BF4',
  'BS5',
  'BC5',
  'BF5',
  'BS6',
  'BC6',
  'BF6',
  'BFINAL'
];

export default function PcTopRoadSection({
  viewBox = '0 0 268 616',
  className = '',
  pawns = [],
  animatingPawnIds,
  kickingPawnIds,
  rollbackPawnIds,
  flippingPawnIds,
  onPawnClick,
  clickablePawnIds,
  pawnComponents
}: RoadSectionProps) {
  return (
    <svg
      viewBox={viewBox}
      className={className}
      overflow="visible"
      xmlns="http://www.w3.org/2000/svg"
      aria-label="Top road section with blue final stretch"
    >
      {/* Row 6: Blue gradient center, gray side squares */}
      <rect
        x="95.58"
        y="403.61"
        width="76.84"
        height="76.84"
        fill="url(#pctt_paint0)"
        stroke="black"
        strokeMiterlimit="10"
      />
      <rect x="101.91" y="409.94" width="64.17" height="64.17" rx="8.06" ry="8.06" fill="white" />
      <rect x="172.42" y="403.61" width="76.84" height="76.84" fill="#efefef" />
      <rect x="18.74" y="403.61" width="76.84" height="76.84" fill="#efefef" />

      {/* Row 5: Blue accent center, dark gray side squares */}
      <rect
        x="95.58"
        y="326.77"
        width="76.84"
        height="76.84"
        fill="#2468ff"
        stroke="black"
        strokeMiterlimit="10"
      />
      <rect x="101.91" y="333.1" width="64.17" height="64.17" rx="8.06" ry="8.06" fill="white" />
      <rect x="172.42" y="326.77" width="76.84" height="76.84" fill="#c4c4c4" />
      <rect x="18.74" y="326.77" width="76.84" height="76.84" fill="#c4c4c4" />

      {/* Row 4: Blue gradient center, gray side squares */}
      <rect
        x="95.58"
        y="249.92"
        width="76.84"
        height="76.84"
        fill="url(#pctt_paint1)"
        stroke="black"
        strokeMiterlimit="10"
      />
      <rect x="101.91" y="256.26" width="64.17" height="64.17" rx="8.06" ry="8.06" fill="white" />
      <rect x="172.42" y="249.92" width="76.84" height="76.84" fill="#efefef" />
      <rect x="18.74" y="249.92" width="76.84" height="76.84" fill="#efefef" />

      {/* Row 3: Blue accent center, dark gray side squares */}
      <rect
        x="95.58"
        y="173.08"
        width="76.84"
        height="76.84"
        fill="#2468ff"
        stroke="black"
        strokeMiterlimit="10"
      />
      <rect x="101.91" y="179.42" width="64.17" height="64.17" rx="8.06" ry="8.06" fill="white" />
      <rect x="172.42" y="173.08" width="76.84" height="76.84" fill="#c4c4c4" />
      <rect x="18.74" y="173.08" width="76.84" height="76.84" fill="#c4c4c4" />

      {/* Row 2: Blue gradient center, gray left, dark right */}
      <rect
        x="95.58"
        y="96.24"
        width="76.84"
        height="76.84"
        fill="url(#pctt_paint2)"
        stroke="black"
        strokeMiterlimit="10"
      />
      <rect x="101.91" y="102.58" width="64.17" height="64.17" rx="8.06" ry="8.06" fill="white" />
      <rect
        x="172.42"
        y="96.24"
        width="76.84"
        height="76.84"
        fill="#2468ff"
        stroke="black"
        strokeMiterlimit="10"
      />
      <rect x="18.74" y="96.24" width="76.84" height="76.84" fill="#efefef" />

      {/* Row 1: Top with rounded corners */}
      <path
        d="M172.42,19.4h76.84v76.84h-66.84c-5.52,0-10-4.48-10-10V19.4h0Z"
        fill="#c4c4c4"
        stroke="black"
        strokeMiterlimit="10"
      />
      <rect x="95.58" y="19.4" width="76.84" height="76.84" fill="#efefef" />
      <path
        d="M18.74,19.4h76.84v66.84c0,5.52-4.48,10-10,10H18.74V19.4h0Z"
        fill="#c4c4c4"
        stroke="black"
        strokeMiterlimit="10"
      />

      {/* Star decoration - shadow layer + white top layer */}
      <path
        d="M65.53,199.87l-7.1-11.67c-.69-1.13-2.33-1.1-2.98.05l-6.75,11.88-13.3,3.15c-1.28.3-1.77,1.88-.88,2.85l9.21,10.09-1.11,13.62c-.11,1.32,1.24,2.26,2.44,1.72l12.44-5.65,12.61,5.27c1.22.51,2.53-.48,2.39-1.79l-1.52-13.58,8.91-10.36c.86-1,.33-2.56-.96-2.82l-13.39-2.75Z"
        fill="#303030"
      />
      <path
        d="M65.48,198.57l-7.1-11.67c-.69-1.13-2.33-1.1-2.98.05l-6.75,11.88-13.3,3.15c-1.28.3-1.77,1.88-.88,2.85l9.21,10.09-1.11,13.62c-.11,1.32,1.24,2.26,2.44,1.72l12.44-5.65,12.61,5.27c1.22.51,2.53-.48,2.39-1.79l-1.52-13.58,8.91-10.36c.86-1,.33-2.56-.96-2.82l-13.39-2.75Z"
        fill="white"
      />

      {/* White center path square in right column row 2 */}
      <rect x="178.75" y="102.58" width="64.17" height="64.17" rx="8.06" ry="8.06" fill="white" />

      {/* Keyboard key labels */}
      <text
        fill="#717272"
        fontFamily="Minecraft, Minecraft"
        fontSize="11.61"
        transform="translate(228.51 245.41)"
      >
        F11
      </text>
      <text
        fill="#717272"
        fontFamily="Minecraft, Minecraft"
        fontSize="11.61"
        transform="translate(229.23 321.3)"
      >
        F8
      </text>
      <text
        fill="#717272"
        fontFamily="Minecraft, Minecraft"
        fontSize="11.61"
        transform="translate(229.23 398.38)"
      >
        F5
      </text>
      <text
        fill="#717272"
        fontFamily="Minecraft, Minecraft"
        fontSize="10.32"
        transform="translate(229.23 473.71) scale(1.12 1)"
      >
        F2
      </text>
      <text
        fill="#717272"
        fontFamily="Minecraft, Minecraft"
        fontSize="10.32"
        transform="translate(66.76 473.71) scale(1.12 1)"
      >
        ESC
      </text>
      <text
        fill="#717272"
        fontFamily="Minecraft, Minecraft"
        fontSize="11.61"
        transform="translate(69.99 89.76)"
      >
        DEL
      </text>
      <text
        fill="#717272"
        fontFamily="Minecraft, Minecraft"
        fontSize="11.61"
        transform="translate(77.5 244.33)"
      >
        F9
      </text>
      <text
        fill="#717272"
        fontFamily="Minecraft, Minecraft"
        fontSize="11.61"
        transform="translate(72.38 167.29)"
      >
        F12
      </text>
      <text
        fill="#717272"
        fontFamily="Minecraft, Minecraft"
        fontSize="11.61"
        transform="translate(77.5 320.93)"
      >
        F6
      </text>
      <text
        fill="#717272"
        fontFamily="Minecraft, Minecraft"
        fontSize="11.61"
        transform="translate(77.5 398.38)"
      >
        F3
      </text>

      {/* Blue gradient triangle at bottom */}
      <polygon
        points="134 595.71 18.74 480.45 249.26 481.18 134 595.71"
        fill="url(#pctt_paint3)"
        stroke="black"
        strokeMiterlimit="10"
      />

      {/* Render pawns at all 19 positions - from top to bottom for 3D overlap effect */}
      {renderMultiplePositions(
        RENDER_ORDER,
        pawns,
        CENTER_SQUARE_BOUNDS,
        undefined,
        1.0,
        animatingPawnIds,
        onPawnClick,
        clickablePawnIds,
        rollbackPawnIds,
        kickingPawnIds,
        pawnComponents,
        flippingPawnIds
      )}

      <defs>
        {/* paint0: Blue gradient for Row 6 center */}
        <linearGradient
          id="pctt_paint0"
          x1="95.58"
          y1="442.03"
          x2="172.42"
          y2="442.03"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#555eff" />
          <stop offset="0.27" stopColor="#525cfb" />
          <stop offset="0.49" stopColor="#4c57f2" />
          <stop offset="0.68" stopColor="#414fe1" />
          <stop offset="0.86" stopColor="#3243ca" />
          <stop offset="1" stopColor="#2237b2" />
        </linearGradient>

        {/* paint1: Blue gradient for Row 4 center */}
        <linearGradient
          id="pctt_paint1"
          x1="95.58"
          y1="288.34"
          x2="172.42"
          y2="288.34"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#555eff" />
          <stop offset="0.27" stopColor="#525cfb" />
          <stop offset="0.49" stopColor="#4c57f2" />
          <stop offset="0.68" stopColor="#414fe1" />
          <stop offset="0.86" stopColor="#3243ca" />
          <stop offset="1" stopColor="#2237b2" />
        </linearGradient>

        {/* paint2: Blue gradient for Row 2 center */}
        <linearGradient
          id="pctt_paint2"
          x1="95.58"
          y1="134.66"
          x2="172.42"
          y2="134.66"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#555eff" />
          <stop offset="0.27" stopColor="#525cfb" />
          <stop offset="0.49" stopColor="#4c57f2" />
          <stop offset="0.68" stopColor="#414fe1" />
          <stop offset="0.86" stopColor="#3243ca" />
          <stop offset="1" stopColor="#2237b2" />
        </linearGradient>

        {/* paint3: Blue gradient for bottom triangle */}
        <linearGradient
          id="pctt_paint3"
          x1="18.74"
          y1="538.08"
          x2="249.26"
          y2="538.08"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#555eff" />
          <stop offset="0.27" stopColor="#525cfb" />
          <stop offset="0.49" stopColor="#4c57f2" />
          <stop offset="0.68" stopColor="#414fe1" />
          <stop offset="0.86" stopColor="#3243ca" />
          <stop offset="1" stopColor="#2237b2" />
        </linearGradient>
      </defs>
    </svg>
  );
}
